import Box from '@components/base/Box'
import { Button } from '@components/inputs/Button'
import { DateTimeInput } from '@components/inputs/DateTimeInput'
import { DropdownInput } from '@components/inputs/DropdownInput'
import { TextAreaInput } from '@components/inputs/TextAreaInput'
import { TextInput } from '@components/inputs/TextInput'
import Modal from '@components/base/Modal'
import {
  EventSession,
  EventStage,
  EventType,
} from '@graphql/__generated/graphql'
import CreateEventLayout from '@layouts/CreateEventLayout'
import { useEventEditor } from '@libs/CreateEvent/useEventEditor'
import React, { useState } from 'react'
import Table from '@components/base/Table'
import TableRowActions from '@components/event/TableRowActions'
import moment from 'moment'
import { ArrayTextInput } from '@components/inputs/ArrayTextInput'
import ProfileImage from '@components/base/ProfileImage'
import useAutosave from '@libs/useAutosave'
import { v4 } from 'uuid'
import {
  formatEventInstant,
  getBrowserTimeZoneId,
  instantToPickerDate,
  resolveLegacyTimeZoneId,
} from '@libs/EventTime'

const getSpeakerIdentity = (speaker: {
  name?: string | null
  email?: string | null
  organization?: string | null
}) => `${speaker?.name ?? ''}::${speaker?.email ?? speaker?.organization ?? ''}`

const SessionsForm = () => {
  const [showModal, setShowModal] = useState(false)
  const [showBreakModal, setShowBreakModal] = useState(false)
  const [sessionModalData, setSessionModalData] = useState<any>(undefined)
  const { formData, setFormData } = useEventEditor()
  const { sessions, stages, event, speakers } = formData ?? {}
  const noData = sessions == undefined || sessions.length === 0
  const isInPerson = formData?.event?.eventType === EventType.InPerson

  const { setHasEdited } = useAutosave()

  const eventTimeZoneId =
    formData?.event?.timeZoneId ??
    resolveLegacyTimeZoneId(formData?.event?.timeZone) ??
    getBrowserTimeZoneId()

  const handleSubmit = async () => {
    if (!sessionModalData) return
    const data = sessionModalData
    const {
      title,
      description,
      startDateTime,
      endDateTime,
      stage,
      speakers: sessionSpeakers,
      editIndex,
      isBreak,
    } = data

    let payload = {
      ...formData,
      sessions: [...(sessions ?? [])],
    }

    let newSession: EventSession = {
      title: title ?? '',
      description: description ?? '',
      startDateTime: startDateTime ?? '',
      endDateTime: endDateTime ?? '',
      stage,
    }

    if (isBreak) newSession.isBreak = true
    if (!isBreak) {
      newSession = {
        ...newSession,

        stage: stages?.find((s) => s.title === stage) as EventStage,
        speakers: (sessionSpeakers
          ?.map((s: { key: string; value: string }) => {
            return speakers?.find((es) => {
              if (getSpeakerIdentity(es) === s.value) return true
            if (es.email === s.value) return true
            return false
          })
          })
          .filter(Boolean) ?? []) as any,
      }
    }

    if (editIndex === undefined) payload.sessions.push(newSession)
    if (editIndex !== undefined) payload.sessions[editIndex] = newSession

    setFormData(payload)
    setHasEdited(true)
    setShowModal(false)
    setShowBreakModal(false)
  }

  const breakModal = (
    <Modal
      show={showBreakModal}
      setShow={setShowBreakModal}
      title={`${
        sessionModalData?.editIndex !== undefined ? 'Update' : 'Add'
      } Break`}
    >
      <TextInput
        required
        label="Title"
        placeholder="Break Session Name"
        value={sessionModalData?.title}
        onChange={(data) => {
          setSessionModalData({ ...sessionModalData, title: data })
        }}
      />
      <TextAreaInput
        className="mt-8"
        label="Description"
        placeholder="Write your description"
        value={sessionModalData?.description}
        onChange={(data) => {
          setSessionModalData({ ...sessionModalData, description: data })
        }}
      />
      <Box className="flex flex-col sm:flex-row justify-between sm:space-x-6">
        <DateTimeInput
          timeZoneId={eventTimeZoneId}
          required
          className="flex-1"
          label="Start Date/Time"
          initialDate={
            instantToPickerDate(
              formData?.event?.startDateTime,
              eventTimeZoneId
            ) ?? undefined
          }
          filterDate={(rawDate) => {
            const date = moment(rawDate)

            const startTime = moment(
              instantToPickerDate(
                formData?.event?.startDateTime,
                eventTimeZoneId
              )
            )

            const endTime = moment(
              instantToPickerDate(formData?.event?.endDateTime, eventTimeZoneId)
            )

            if (date.isBefore(startTime, 'day')) return false
            if (date.isAfter(endTime, 'day')) return false
            return true
          }}
          filterTime={(rawTime) => {
            const date = moment(rawTime)
            const startTime = moment(
              instantToPickerDate(
                formData?.event?.startDateTime,
                eventTimeZoneId
              )
            )
            const endTime = moment(
              instantToPickerDate(formData?.event?.endDateTime, eventTimeZoneId)
            )
            if (date.isBefore(startTime, 'minute')) return false
            if (date.isAfter(endTime, 'minute')) return false
            return true
          }}
          value={sessionModalData?.startDateTime}
          onChange={(data) => {
            setSessionModalData({ ...sessionModalData, startDateTime: data })
          }}
        />
        <DateTimeInput
          timeZoneId={eventTimeZoneId}
          initialDate={
            instantToPickerDate(
              formData?.event?.startDateTime,
              eventTimeZoneId
            ) ?? undefined
          }
          required
          className="flex-1"
          label="End Date/Time"
          filterDate={(rawDate) => {
            const date = moment(rawDate)

            const startTime = moment(
              instantToPickerDate(
                formData?.event?.startDateTime,
                eventTimeZoneId
              )
            )
            const endTime = moment(
              instantToPickerDate(formData?.event?.endDateTime, eventTimeZoneId)
            )

            if (date.isBefore(startTime, 'day')) return false
            if (date.isAfter(endTime, 'day')) return false
            return true
          }}
          filterTime={(rawTime) => {
            const date = moment(rawTime)
            const startTime = moment(
              instantToPickerDate(
                formData?.event?.startDateTime,
                eventTimeZoneId
              )
            )
            const endTime = moment(
              instantToPickerDate(formData?.event?.endDateTime, eventTimeZoneId)
            )

            if (date.isBefore(startTime, 'minute')) return false
            if (date.isAfter(endTime, 'day')) return false
            return true
          }}
          value={sessionModalData?.endDateTime}
          onChange={(data) => {
            setSessionModalData({ ...sessionModalData, endDateTime: data })
          }}
        />
      </Box>
      <Button
        className="my-4"
        title={`${
          sessionModalData?.editIndex !== undefined ? 'Update' : 'Add'
        } Break`}
        type="primary"
        buttonType="button"
        onClick={handleSubmit}
      />
    </Modal>
  )
  const modal = (
    <Modal
      show={showModal}
      setShow={(visible) => {
        setShowModal(visible)
      }}
      title={`${
        sessionModalData?.editIndex !== undefined ? 'Update' : 'Create'
      } Session`}
    >
      <TextInput
        required
        label="Title"
        placeholder="Session Name"
        value={sessionModalData?.title}
        onChange={(data) => {
          setSessionModalData({ ...sessionModalData, title: data })
        }}
      />
      <Box className="mt-6">
        <ArrayTextInput
          label="Speakers"
          placeholder="Speaker Name"
          options={
            (speakers ?? []).map((s) => ({
              id: getSpeakerIdentity(s),
              label: s?.name,
            })) ?? []
          }
          value={sessionModalData?.speakers}
          onChange={(data) => {
            setSessionModalData({ ...sessionModalData, speakers: data })
          }}
        />
      </Box>
      <TextAreaInput
        className="mt-6 mb-6"
        label="Description"
        placeholder="Write your description"
        value={sessionModalData?.description}
        onChange={(data) => {
          setSessionModalData({ ...sessionModalData, description: data })
        }}
      />
      <Box className="flex flex-col sm:flex-row justify-between sm:space-x-6 z-20">
        <DateTimeInput
          timeZoneId={eventTimeZoneId}
          required
          className="flex-1"
          initialDate={
            instantToPickerDate(event?.startDateTime, eventTimeZoneId) ??
            undefined
          }
          label="Start Date/Time"
          filterDate={(rawDate) => {
            const date = moment(rawDate)

            const startTime = moment(
              instantToPickerDate(
                formData?.event?.startDateTime,
                eventTimeZoneId
              )
            )
            const endTime = moment(
              instantToPickerDate(formData?.event?.endDateTime, eventTimeZoneId)
            )

            if (date.isBefore(startTime, 'day')) return false
            if (date.isAfter(endTime, 'day')) return false
            return true
          }}
          filterTime={(rawTime) => {
            const date = moment(rawTime)
            const startTime = moment(
              instantToPickerDate(
                formData?.event?.startDateTime,
                eventTimeZoneId
              )
            )
            const endTime = moment(
              instantToPickerDate(formData?.event?.endDateTime, eventTimeZoneId)
            )
            if (date.isBefore(startTime, 'minute')) return false
            if (date.isAfter(endTime, 'minute')) return false
            return true
          }}
          value={sessionModalData?.startDateTime}
          onChange={(data) => {
            setSessionModalData({ ...sessionModalData, startDateTime: data })
          }}
        />
        <DateTimeInput
          timeZoneId={eventTimeZoneId}
          required
          validations={{
            greaterThanDate: sessionModalData?.startDateTime,
          }}
          className="flex-1"
          initialDate={
            instantToPickerDate(
              sessionModalData?.startDateTime,
              eventTimeZoneId
            ) ?? undefined
          }
          label="End Date/Time"
          filterDate={(rawDate) => {
            const date = moment(rawDate)

            const startTime = moment(
              instantToPickerDate(
                formData?.event?.startDateTime,
                eventTimeZoneId
              )
            )
            const endTime = moment(
              instantToPickerDate(formData?.event?.endDateTime, eventTimeZoneId)
            )

            if (date.isBefore(startTime, 'day')) return false
            if (date.isAfter(endTime, 'day')) return false
            return true
          }}
          filterTime={(rawTime) => {
            const date = moment(rawTime)
            const startTime = moment(
              instantToPickerDate(
                formData?.event?.startDateTime,
                eventTimeZoneId
              )
            )
            const endTime = moment(
              instantToPickerDate(formData?.event?.endDateTime, eventTimeZoneId)
            )
            if (date.isBefore(startTime, 'minute')) return false
            if (date.isAfter(endTime, 'minute')) return false
            return true
          }}
          value={sessionModalData?.endDateTime}
          onChange={(data) => {
            setSessionModalData({ ...sessionModalData, endDateTime: data })
          }}
        />
      </Box>
      <DropdownInput
        label="Stage"
        options={stages?.map((s) => s.title) ?? []}
        required={!isInPerson}
        placeholder="Select a stage"
        className={`${isInPerson ? 'hidden' : ''}`}
        value={sessionModalData?.stage}
        onChange={(data) => {
          setSessionModalData({ ...sessionModalData, stage: data })
        }}
      />
      <Button
        className="my-4"
        title={`${
          sessionModalData?.editIndex !== undefined ? 'Update' : 'Create'
        } session`}
        type="primary"
        buttonType="button"
        onClick={() => {
          setSessionModalData({ ...sessionModalData, isBreak: false })
          handleSubmit()
        }}
      />
    </Modal>
  )

  return (
    <>
      {modal}
      {breakModal}
      <Box className="w-full">
        <Box className="mt-6 mb-4 flex">
          <Button
            onClick={() => {
              setSessionModalData(undefined)
              setShowModal(true)
            }}
            title="Add Session"
            type="secondary"
            size="small"
          />
          <Button
            onClick={() => {
              setSessionModalData({ isBreak: true })
              setShowBreakModal(true)
            }}
            title="Add Break"
            type="secondary"
            size="small"
            className="ml-2"
          />
        </Box>

        <Table
          tableHeading={[
            'Name',
            'Start Time',
            'End Time',
            ...(isInPerson ? [] : ['Stage']),
            'Speakers',
            '',
          ]}
          rows={[...(sessions ?? [])]
            .map((session, originalIndex) => ({ session, originalIndex }))
            .sort((a, b) => {
              const aSession = a.session
              const bSession = b.session

              if (!aSession.startDateTime || !bSession.startDateTime) return 0

              const aStart = moment(aSession.startDateTime)
              const bStart = moment(bSession.startDateTime)

              if (aStart.isAfter(bStart)) return 1
              if (bStart.isAfter(aStart)) return -1

              return 0
            })
            .map(({ session, originalIndex }, index) => [
              session?.title,
              formatEventInstant(
                session?.startDateTime,
                'MMM DD, h:mmA',
                formData?.event
              ),
              formatEventInstant(
                session?.endDateTime,
                'MMM DD, h:mmA',
                formData?.event
              ),
              !session?.isBreak ? session?.stage?.title! : 'Break',
              <Box className="flex -space-x-5 flex-wrap" key={index}>
                {session?.speakers?.map((s) => {
                  return (
                    <ProfileImage
                      key={s?.email ?? v4()}
                      imageUrl={s?.profileImage ?? null}
                    />
                  )
                })}
              </Box>,
              <TableRowActions
                key={index}
                confirmModal={{
                  title: 'Are you sure?',
                  content:
                    'Are you sure you want to delete this? This cannot be undone.',
                }}
                editClicked={() => {
                  setSessionModalData({
                    title: session?.title,
                    description: session?.description,
                    startDateTime: session?.startDateTime,
                    endDateTime: session?.endDateTime,
                    stage: session?.stage?.title,
                    speakers: session?.speakers?.map((s) => ({
                      key: s?.name,
                      value: getSpeakerIdentity(s),
                    })),
                    editIndex: originalIndex,
                    isBreak: session?.isBreak,
                  })
                  if (session?.isBreak) setShowBreakModal(true)
                  if (!session?.isBreak) setShowModal(true)
                }}
                deleteClicked={() => {
                  setHasEdited(true)
                  setFormData({
                    ...formData,
                    sessions: sessions?.filter((_, i) => i !== originalIndex),
                  })
                }}
              />,
            ])}
        />
        {noData && (
          <Box>
            <Box className="text-sm mt-8 text-center">
              {'No sessions have been created'}
            </Box>
            <hr className="my-6 border-gray-600 opacity-50" />
          </Box>
        )}
      </Box>
    </>
  )
}

SessionsForm.Layout = CreateEventLayout

export default SessionsForm
